<template>
  <PageContainer>
    <!-- Summary -->
    <div v-if="!loading">
      <h3>Past 7 days</h3>
      <div class="flex flex-row justify-between">
        <div class="min-w-2"></div>
        <a @click="goToData('consistency')">
          <MetricSleepConsistency
            :dateFrom='dateFrom'
            :dateTo='dateTo'
          />
        </a>
        <div class="min-w-2"></div>
        <a @click="goToData('duration')">
          <MetricSleepDuration
            :dateFrom='dateFrom'
            :dateTo='dateTo'
          />
        </a>
        <div class="min-w-2"></div>
        <a @click="goToData('quality')">
          <MetricSleepQuality
            :dateFrom='dateFrom'
            :dateTo='dateTo'
          />
        </a>
        <div class="min-w-2"></div>
      </div>
    </div>

    <!-- Loading -->
    <!-- TODO: Pull out into component. -->
    <div v-if="loading" class="fixed inset-0 flex flex-col items-center justify-center">
      <object class="h-8 w-auto pointer-events-none animate-ping" :data="logoImage" type="image/svg+xml"></object>
      <p class="mt-4 text-gray-400">Loading...</p>
    </div>

    <!-- Streak -->
    <div v-if="!loading && planStore.active && !(streakStore.daysLeft === 0)" class="flex flex-row gap-4 items-top mt-4 p-4 rounded-lg border-2 border-teal-200 bg-teal-100/20 text-left">
      <div class="text-2xl pt-2">🔥</div>
      <div class="w-full">
        <!-- TODO: Add condition for finished streak (but not yet deactivated), show finished modal. -->
        <h3>Your bed time streak</h3>
          <p v-if="streakStore.dayOne">Start your streak off strong, go to bed before {{ formatTime(target.timeBed) }} tonight!</p>
          <p v-else>Keep your streak alive, go to bed before {{ formatTime(target.timeBed) }} tonight!</p>
          <StreakComponent/>
      </div>
    </div>

    <!-- Insights -->
    <div v-if="!loading && !planStore.active" class="flex flex-row gap-4 items-top mt-4 p-4 rounded-lg bg-sky-100 text-left">
      <div class="text-2xl pt-2">🔍</div>
      <div class="w-full">
        <h3>Insights</h3>
        <p>Your <span class="font-medium">sleep consistency</span> is poor.</p>
        <ul :class="{ hidden: !insightExpanded }">
          <li>There is a high variation in the time you go to bed ({{ sleepConsistency?.bedTimeRange }} hours).</li>
          <li>The average time you go to bed is around {{ sleepConsistency?.bedTimeAverage }}.</li>
        </ul>
        <button @click="$router.push('/data/consistency')" class="button-secondary" :class="{ hidden: !insightExpanded }">Learn more</button>
      </div>
      <img @click="insightExpanded = true" src="@/assets/chevron_down.png" class="w-8 place-self-center opacity-50" :class="{ hidden: insightExpanded }">
    </div>

    <!-- Recommendation -->
    <div v-if="!loading && !planStore.active" class="flex flex-row gap-4 items-top mt-4 p-4 rounded-lg bg-teal-100/75 text-left">
      <div class="text-2xl pt-2">💡</div>
      <div>
        <h3>Recommendation</h3>
        <div>
          <p>Your top priority should be to focus on sleep consistency. We've created a personalised plan for you!</p>
          <LoadingButton @action-function="startPlan()">
            Start plan
          </LoadingButton>
        </div>
      </div>
    </div>

    <!-- Tasklist -->
    <div v-if="!loading && planStore.active && !(streakStore.daysLeft === 0)" class="flex flex-row gap-4 items-top mt-4 p-4 rounded-lg bg-sky-100 text-left">
      <div class="text-2xl pt-2">📋</div>
      <div class="w-full">
        <h3>Today's checklist</h3>
        <div class="grid grid-cols-1 gap-2">
          <div v-for="tool in planStore.activeTools" :key="tool" @click="openToolModal(tool.id)" class="flex flex-row items-center justify-between w-full bg-white p-4 rounded-lg border border-slate-400">
            <div class="mr-2">{{ tool.icon }}</div>
            <div class="w-full text-left">{{ tool.name }}</div>
            <img src="@/assets/chevron_right.png" class="w-8 -my-2 place-self-center opacity-50">
          </div>
          <div class="w-full bg-white p-4 rounded-lg border border-slate-400">🔥 Be in bed before {{ formatTime(target.timeBed) }}</div>
        </div>
        <button @click="goToPlan()" class="button-secondary">View plan</button>
      </div>
    </div>

    <!-- Stage complete -->
    <div v-if="!loading && planStore.active && (streakStore.daysLeft === 0)" class="flex flex-row gap-4 items-top mt-4 p-4 rounded-lg bg-sky-100 text-left border-2 border-teal-200">
      <div class="text-2xl pt-2">📋</div>
      <div class="w-full">
        <h3>Stage {{ planStore.activeStageIndex }} finished</h3>
        <p>Lets review how you did this week!</p>
        <button @click="showStageReportModal()">View report</button>
      </div>
    </div>
    <!-- Chat -->
    <!--
    <div class="fixed inset-x-0 bottom-0 w-full max-w-screen-sm left-1/2 -translate-x-1/2 px-4">
      <div class="flex flex-row justify-between px-8 pt-4 pb-8 rounded-t-3xl bg-gray-100 cursor-pointer">
        <input type="text" name="chat" placeholder="Chat with coach" class="grow placeholder:text-slate-500 bg-transparent ring-0 active:none focus:outline-none focus:border-none">
        <div class="text-xl text-slate-500 cursor-pointer">&#8963;</div>
      </div>
    </div>
    -->
  </PageContainer>

  <!-- MODALS -->
  <SleepTargets
    v-if="sleepTargetModal"
    v-model="sleepTargetModal"
    @save-function="updateUserTargets"
  />
</template>

<script>
import { defineAsyncComponent } from 'vue'
import { get7DaysAgo, time24To12, dateToday } from '@/helpers/time'
import PageContainer from '@/components/PageContainer'
import MetricSleepConsistency from '@/components/MetricSleepConsistency'
import MetricSleepDuration from '@/components/MetricSleepDuration'
import MetricSleepQuality from '@/components/MetricSleepQuality'
import LoadingButton from '@/components/LoadingButton'
import StreakComponent from '@/components/StreakComponent'
import Modal from '@/models/modal'
import { mapStores } from 'pinia'
import { useUserStore } from '@/stores/user'
import { useStreakStore } from '@/stores/streak'
import { useSleepStore } from '@/stores/sleep'
import { useToolStore } from '@/stores/tool'
import { usePlanStore } from '@/stores/plan'
import { useModalStore } from '@/stores/modal'

export default {
  name: 'HomePage',
  components: {
    PageContainer,
    MetricSleepConsistency,
    MetricSleepDuration,
    MetricSleepQuality,
    LoadingButton,
    StreakComponent,
    SleepTargets: defineAsyncComponent(
      () => import('@/components/modals/SleepTargets'),
    ),
  },
  data() {
    const dates = get7DaysAgo()

    return {
      dateFrom: dates.start,
      dateTo: dates.end,
      sleepConsistency: undefined,
      insightExpanded: false,
      sleepTargetModal: false,
      target: {},
      activePlan: false,
      activeStageComplete: false,
      activeStageIndex: undefined,
      loading: true,
      logoImage: require('@/assets/logo.svg'),
      // TODO: Implement loading for boxes on the page.
      // loading: true,
    }
  },
  async beforeMount() {
    // TODO: Determine which functions can be run synchronously.
    this.syncFitbit()

    // Load modal store.
    this.loadModals()

    // Initialise global stores with data.
    this.loadSleepConsistency()
    this.loadSleepDuration()
    this.loadSleepQuality()

    // TODO: Only load if we are showing sleep targets.
    await this.loadPlan()
    // TODO: Only load if there is an active streak to show.
    await this.loadStreak()

    // Finished loading the page.
    this.loading = false
  },
  methods: {
    syncFitbit() {
      this.userStore.user.syncFitbit()
    },
    async loadSleepConsistency() {
      this.sleepConsistency = await this.userStore.user.getSleepConsistencyScore(this.dateFrom, this.dateTo)
      this.sleepConsistency.bedTimeRange = Math.round(this.sleepConsistency.bedTimeRange / 60)
      // TODO: Fix this for the case when consistency score is 0, 0, 0 (no sleep record).
      this.sleepConsistency.bedTimeAverage = time24To12(this.sleepConsistency.bedTimeAverage).replace(/^0+/, '')
    },
    async loadSleepDuration() {
      await this.userStore.user.getSleepDuration(this.dateFrom, this.dateTo)
    },
    async loadSleepQuality() {
      // TODO: Initialise store.
    },
    async loadStreak() {
      // If there is an active streak that hasn't been viewed today, show the
      // streak start modal on day one, streak progress modal on other days.
      await this.streakStore.load

      // TODO: Move to `AppModalSelector`.
      const viewed = localStorage.getItem('streak:viewed')
      if (this.streakStore.active
        && viewed !== dateToday()
        && !this.streakStore.dayOne
        && this.streakStore.daysLeft > 0) {
        this.modalStore.show(Modal.MODAL_STREAK_PROGRESS)
      }
    },
    async loadPlan() {
      // Get active plan if it exists.
      await this.planStore.load()
      if (!this.planStore.active) return

      // Load targets from plan.
      this.target.timeBed = this.planStore.activeStage.target.bedTime
      // this.target.timeWake = stage?.target.targetWakeTime
    },
    loadModals() {
      this.modalStore.load()
    },
    updateUserTargets(bed, wake) {
      this.target.timeBed = bed
      this.target.timeWake = wake
    },
    formatTime(t) {
      return time24To12(t)
    },
    startPlan() {
      this.$router.push('/plan')
    },
    showStageReportModal() {
      this.modalStore.show(Modal.MODAL_STAGE_REPORT)
    },
    // TODO: Move to `AppModalSelector`.
    openToolModal(id) {
      this.modalStore.show(Modal.MODAL_TOOL, {
        toolObject: this.toolStore.getTool(id),
        toolNew: !localStorage.getItem('tool-' + id + ':viewed'),
      })
    },
    goToData(metric) {
      this.$router.push('/data/' + metric)
    },
    goToPlan() {
      this.$router.push('/plan/outline')
    },
  },
  computed: {
    ...mapStores(useUserStore, useStreakStore, useSleepStore, useToolStore, usePlanStore, useModalStore),
  },
}
</script>